import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, X, MapPin, Clock, Repeat, Eye, LayoutGrid, CheckCircle2, AlertTriangle, Send, Mail, Link2, Users, UserCheck } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import { DialogoHost, conferma, avvisa } from '../../components/Dialogo';
import InFinestra from '../../components/InFinestra';
import useIsMobile from '../../utils/useIsMobile';

// Calendario attività (25/10/2026, titolare): calendario globale della società, NON quello delle presenze.
// Ogni evento ha sede, tag e visibilità multipla (spunte): Staff / Utenti (portale) / Pubblico (link condivisi senza login).
// Ripetizione come gli slot delle presenze: dal giorno al giorno, giorni della settimana → un evento per data,
// legati in una serie (modifica/elimina: solo questo, da qui in poi, tutta la serie).
// Scheda "Link condivisi": pagina /calendario-pubblico/<token> + indirizzo .ics per Google Calendar, filtrati per sede/tag.
// Passi successivi: vista utenti nel portale, widget.
const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const GIORNI = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
const quando = (d) => new Date(d).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const giornoCorto = (g) => new Date(g + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
const PALETTE = ['#F4600C','#3B82F6','#22C55E','#A855F7','#F59E0B','#EC4899','#06B6D4','#64748B'];
export const VISIBILITA = [
  ['STAFF', 'Staff', 'Chi lavora nel gestionale'],
  ['UTENTI', 'Utenti', 'Chi entra nel portale'],
  ['PUBBLICO', 'Pubblico', 'Chiunque abbia un link condiviso'],
];
const STILE_VIS = { STAFF: 'tagGrigio', UTENTI: 'tagBlu', PUBBLICO: 'tagVerde' };
const errore = (e, def) => e?.response?.data?.error || def;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const giornoLungo = (s) => { const t = new Date(s + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }); return t[0].toUpperCase() + t.slice(1); };
const giornoBreve = (s) => new Date(s + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
const colore = (ev) => ev.colore || ev.luogo?.colore || '#64748B';
const inizio = (ev) => ev.data.slice(0, 10);
const fine = (ev) => (ev.data_fine || ev.data).slice(0, 10);
const orario = (ev) => ev.ora_inizio ? (ev.ora_fine ? `${ev.ora_inizio}–${ev.ora_fine}` : ev.ora_inizio) : 'Tutto il giorno';

export default function CalendarioAttivita() {
  const { user } = useAuth();
  // ?scheda=link: dall'avviso a video "Ho visto"
  const { search } = useLocation();
  const [scheda, setScheda] = useState(() => new URLSearchParams(search).get('scheda') === 'link' ? 'link' : 'calendario');
  useEffect(() => { if (new URLSearchParams(search).get('scheda') === 'link') setScheda('link'); }, [search]);
  if (!puoFare(user, 'calendario', 'view')) return <div style={s.pagina}><div style={s.vuoto}>Serve il permesso Calendario attività (Impostazioni → Ruoli).</div></div>;
  return (
    <div style={s.pagina}>
      <DialogoHost />
      <h1 style={s.titolo}>Calendario attività</h1>
      <div style={s.sub}>Gli eventi della società: tornei, corsi, feste, campi occupati. Per ognuno scegli la sede, i tag e chi lo vede.</div>
      <div style={s.schede}>
        <button style={{ ...s.scheda, ...(scheda === 'calendario' ? s.schedaOn : {}) }} onClick={() => setScheda('calendario')}>Calendario</button>
        <button style={{ ...s.scheda, ...(scheda === 'sedi' ? s.schedaOn : {}) }} onClick={() => setScheda('sedi')}>Sedi</button>
        {puoFare(user, 'calendario', 'edit') && <button style={{ ...s.scheda, ...(scheda === 'link' ? s.schedaOn : {}) }} onClick={() => setScheda('link')}>Link condivisi</button>}
        {user?.ruolo === 'ROOT' && <button style={{ ...s.scheda, ...(scheda === 'ruoli' ? s.schedaOn : {}) }} onClick={() => setScheda('ruoli')}>Ruoli staff</button>}
      </div>
      {scheda === 'sedi' ? <Sedi /> : scheda === 'link' ? <LinkCondivisi /> : scheda === 'ruoli' ? <RuoliStaff /> : <Calendario />}
    </div>
  );
}

// ─── Calendario ────────────────────────────────────────────────────────────────────────
function Calendario() {
  const { user } = useAuth();
  const mobile = useIsMobile();
  const oggi = iso(new Date());
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [mese, setMese] = useState(new Date().getMonth());
  const [vista, setVista] = useState(() => { try { return localStorage.getItem('calendario_vista') || 'mese'; } catch { return 'mese'; } });
  const [eventi, setEventi] = useState([]);
  const [luoghi, setLuoghi] = useState([]);
  const [tags, setTags] = useState([]);
  const [filtro, setFiltro] = useState({ luogo: '', tag: '', vis: '' });
  const [giornoSel, setGiornoSel] = useState(oggi);
  const [form, setForm] = useState(null); // { evento?, data? }
  const [err, setErr] = useState('');
  const puoCreare = puoFare(user, 'calendario', 'create');

  // Griglia: da lunedì della prima settimana, 6 settimane
  const celle = useMemo(() => {
    const primo = new Date(anno, mese, 1);
    const start = new Date(anno, mese, 1 - ((primo.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [anno, mese]);

  const carica = useCallback(() => {
    const p = new URLSearchParams({ da: iso(celle[0]), a: iso(celle[41]) });
    if (filtro.luogo) p.set('luogo_id', filtro.luogo);
    if (filtro.tag) p.set('tag', filtro.tag);
    return api.get('/calendario?' + p).then(r => { setEventi(r.data); setErr(''); }).catch(e => setErr(errore(e, 'Calendario non caricato')));
  }, [celle, filtro.luogo, filtro.tag]);
  const caricaListe = useCallback(() => Promise.all([
    api.get('/calendario/luoghi').then(r => setLuoghi(r.data)),
    api.get('/calendario/tags').then(r => setTags(r.data)),
  ]).catch(() => {}), []);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { caricaListe(); }, [caricaListe]);

  const visibili = useMemo(() => !filtro.vis ? eventi
    : filtro.vis === 'BOZZA' ? eventi.filter(e => e.bozza)
    : filtro.vis === 'CONFERMATI' ? eventi.filter(e => !e.bozza)
    : eventi.filter(e => e.visibile_a.includes(filtro.vis)), [eventi, filtro.vis]);
  const delGiorno = useCallback((g) => visibili
    .filter(e => inizio(e) <= g && fine(e) >= g)
    .sort((a, b) => (a.ora_inizio || '').localeCompare(b.ora_inizio || '')), [visibili]);

  const cambiaMese = (d) => {
    let m = mese + d, a = anno;
    if (m < 0) { m = 11; a--; } else if (m > 11) { m = 0; a++; }
    setMese(m); setAnno(a);
  };
  const vaiOggi = () => { const n = new Date(); setAnno(n.getFullYear()); setMese(n.getMonth()); setGiornoSel(oggi); };
  const scegliVista = (v) => { setVista(v); try { localStorage.setItem('calendario_vista', v); } catch {} };
  const salvato = () => { setForm(null); carica(); caricaListe(); };

  // Elenco del mese: un evento di più giorni compare una volta, al primo giorno nel mese
  const meseIso = `${anno}-${String(mese + 1).padStart(2, '0')}`;
  const elenco = useMemo(() => {
    const per = {};
    for (const e of visibili) {
      const g = inizio(e) < meseIso + '-01' ? meseIso + '-01' : inizio(e);
      if (g.slice(0, 7) !== meseIso) continue;
      (per[g] = per[g] || []).push(e);
    }
    return Object.entries(per).sort(([a], [b]) => a.localeCompare(b));
  }, [visibili, meseIso]);

  return (
    <>
      {err && <div style={s.err}>{err}</div>}
      <div style={s.barra}>
        <div style={s.nav}>
          <button style={s.icona} onClick={() => cambiaMese(-1)} aria-label="Mese prima"><ChevronLeft size={20} /></button>
          <div style={s.mese}>{MESI[mese]} {anno}</div>
          <button style={s.icona} onClick={() => cambiaMese(1)} aria-label="Mese dopo"><ChevronRight size={20} /></button>
          <button style={s.btn} onClick={vaiOggi}>Oggi</button>
        </div>
        <div style={s.schedeMini}>
          {[['mese', 'Mese'], ['elenco', 'Elenco']].map(([k, l]) =>
            <button key={k} style={{ ...s.scheda, ...(vista === k ? s.schedaOn : {}) }} onClick={() => scegliVista(k)}>{l}</button>)}
        </div>
        <div style={{ flex: 1 }} />
        {puoCreare && <button style={s.btnPrimario} onClick={() => setForm({ data: giornoSel })}><Plus size={15} /> Nuovo evento</button>}
      </div>
      <div style={s.filtri}>
        <select style={s.select} value={filtro.luogo} onChange={e => setFiltro(f => ({ ...f, luogo: e.target.value }))}>
          <option value="">Tutte le sedi</option>
          {luoghi.map(l => <option key={l.id} value={l.id}>{l.nome}{l.attivo ? '' : ' (non attiva)'}</option>)}
          <option value="nessuna">Senza sede</option>
        </select>
        <select style={s.select} value={filtro.tag} onChange={e => setFiltro(f => ({ ...f, tag: e.target.value }))}>
          <option value="">Tutti i tag</option>
          {tags.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={s.select} value={filtro.vis} onChange={e => setFiltro(f => ({ ...f, vis: e.target.value }))}>
          <option value="">Qualsiasi visibilità</option>
          {VISIBILITA.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          <option value="BOZZA">Solo bozze</option>
          <option value="CONFERMATI">Solo confermati</option>
        </select>
        {(filtro.luogo || filtro.tag || filtro.vis) && <button style={s.link} onClick={() => setFiltro({ luogo: '', tag: '', vis: '' })}><X size={14} /> Togli filtri</button>}
      </div>

      {vista === 'mese' ? (
        <div style={{ ...s.colonne, flexDirection: mobile ? 'column' : 'row' }}>
          <div style={s.boxGriglia}>
            <div style={s.griglia7}>{GIORNI.map(g => <div key={g} style={s.testaGiorno}>{g}</div>)}</div>
            <div style={s.griglia7}>
              {celle.map(d => {
                const g = iso(d), evs = delGiorno(g), fuori = d.getMonth() !== mese;
                const sel = g === giornoSel, ogg = g === oggi;
                return (
                  <button key={g} onClick={() => setGiornoSel(g)} onDoubleClick={() => puoCreare && setForm({ data: g })}
                    style={{ ...s.cella, minHeight: mobile ? 52 : 96, opacity: fuori ? 0.45 : 1, ...(sel ? s.cellaSel : {}) }}>
                    <span style={{ ...s.numero, ...(ogg ? s.numeroOggi : {}) }}>{d.getDate()}</span>
                    {mobile
                      ? <span style={s.puntini}>{evs.slice(0, 4).map(e => <span key={e.id} style={{ ...s.puntino, background: e.bozza ? 'transparent' : colore(e), ...(e.bozza ? { border: `1.5px dashed ${colore(e)}` } : {}) }} />)}</span>
                      : <>
                          {evs.slice(0, 3).map(e => (
                            <span key={e.id} title={e.bozza ? 'BOZZA · ' + e.titolo : e.titolo} style={{ ...s.chip, borderLeftColor: colore(e), background: colore(e) + '18', ...(e.bozza ? s.chipBozza : {}) }}>
                              {e.bozza && <b>? </b>}{e.ora_inizio && <b>{e.ora_inizio} </b>}{e.titolo}
                            </span>
                          ))}
                          {evs.length > 3 && <span style={s.altri}>+{evs.length - 3} altri</span>}
                        </>}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ ...s.boxGiorno, ...(mobile ? { maxWidth: 'none' } : {}) }}>
            <div style={s.testaCol}>
              <div style={s.titoletto}>{giornoLungo(giornoSel)}</div>
              {puoCreare && <button style={s.btn} onClick={() => setForm({ data: giornoSel })}><Plus size={14} /> Aggiungi</button>}
            </div>
            {delGiorno(giornoSel).length === 0
              ? <div style={s.dim}>Nessun evento{(filtro.luogo || filtro.tag || filtro.vis) ? ' con questi filtri' : ''}.</div>
              : delGiorno(giornoSel).map(e => <RigaEvento key={e.id} ev={e} onApri={() => setForm({ evento: e })} />)}
          </div>
        </div>
      ) : (
        <div style={s.boxElenco}>
          {elenco.length === 0 && <div style={s.dim}>Nessun evento in {MESI[mese].toLowerCase()}.</div>}
          {elenco.map(([g, evs]) => (
            <div key={g} style={s.gruppoGiorno}>
              <div style={{ ...s.titoletto, ...(g === oggi ? { color: '#C24B08' } : {}) }}>{giornoLungo(g)}</div>
              {evs.map(e => <RigaEvento key={e.id} ev={e} onApri={() => setForm({ evento: e })} />)}
            </div>
          ))}
        </div>
      )}

      {form && <FormEvento {...form} luoghi={luoghi.filter(l => l.attivo || l.id === form.evento?.luogo?.id)} tags={tags}
        onChiudi={() => setForm(null)} onSalvato={salvato} onPresenza={carica} />}
    </>
  );
}

export function RigaEvento({ ev, onApri }) {
  const piuGiorni = ev.data_fine && fine(ev) !== inizio(ev);
  return (
    <button style={{ ...s.rigaEv, borderLeftColor: colore(ev), ...(ev.bozza ? s.rigaBozza : {}) }} onClick={onApri}>
      <div style={s.rigaTitolo}>{ev.bozza && <span style={{ ...s.tag, ...s.tagBozza, marginRight: 6 }}>BOZZA</span>}{ev.titolo}</div>
      <div style={s.rigaInfo}>
        <span style={s.info}><Clock size={13} /> {orario(ev)}{piuGiorni && ` · ${giornoBreve(inizio(ev))} → ${giornoBreve(fine(ev))}`}</span>
        {ev.luogo && <span style={s.info}><MapPin size={13} /> {ev.luogo.nome}</span>}
        {ev.campi && <span style={s.info}><LayoutGrid size={13} /> {ev.campi} {ev.campi === 1 ? 'campo' : 'campi'}</span>}
        {ev.serie_id && <span style={s.info}><Repeat size={13} /> si ripete</span>}
      </div>
      <div style={s.rigaInfo}>
        {VISIBILITA.filter(([k]) => ev.visibile_a.includes(k)).map(([k, l]) =>
          <span key={k} style={{ ...s.tag, ...s[STILE_VIS[k]] }}><Eye size={11} style={{ verticalAlign: -1 }} /> {l}</span>)}
        {ev.tags.map(t => <span key={t} style={{ ...s.tag, ...s.tagArancio }}>{t}</span>)}
      </div>
      {ev.presenze_staff?.length > 0 && (
        <div style={s.rigaInfo}>
          <span style={s.info}><Users size={13} /></span>
          {ev.presenze_staff.map(p => <span key={p.user_id} title={`${p.user.nome} · ${p.ruolo.nome}${p.nota ? ` · ${p.nota}` : ''}`}
            style={{ ...s.tag, background: 'var(--bg-input)', color: 'var(--text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: p.ruolo.colore || '#64748B', flexShrink: 0 }} />{p.user.nome} · {p.ruolo.nome}</span>)}
        </div>
      )}
      {ev.note && <div style={s.note}>{ev.note}</div>}
    </button>
  );
}

// ─── Staff all'evento (27/10, titolare): ognuno segna la sua presenza col ruolo; i ruoli li decide ROOT ──────
function StaffEvento({ evento, onCambio }) {
  const { user } = useAuth();
  const puoAltri = puoFare(user, 'calendario', 'edit');
  const [lista, setLista] = useState(evento.presenze_staff || []);
  const [ruoli, setRuoli] = useState([]);
  const mia = lista.find(p => p.user_id === user.id);
  const [scelta, setScelta] = useState({ ruolo_id: mia?.ruolo.id || '', nota: mia?.nota || '' });
  const [aperto, setAperto] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { api.get('/calendario/ruoli-staff').then(r => setRuoli(r.data)).catch(() => {}); }, []);
  const scegliibili = ruoli.filter(r => r.attivo || r.id === mia?.ruolo.id);

  const salva = async () => {
    if (!scelta.ruolo_id) { setErr('Scegli il ruolo'); return; }
    try {
      await api.put(`/calendario/${evento.id}/presenza`, scelta);
      const r = ruoli.find(x => x.id === +scelta.ruolo_id);
      const nuova = { user_id: user.id, nota: scelta.nota.trim() || null, user: { nome: user.nome }, ruolo: { id: r.id, nome: r.nome, colore: r.colore, ordine: r.ordine } };
      setLista(l => mia ? l.map(p => p.user_id === user.id ? nuova : p) : [...l, nuova]);
      setAperto(false); setErr(''); onCambio?.();
    } catch (er) { setErr(errore(er, 'Presenza non salvata')); }
  };
  const togli = async (p) => {
    const mio = p.user_id === user.id;
    if (!await conferma(mio ? 'Togliere la tua presenza da questo evento?' : `Togliere la presenza di ${p.user.nome}?`, { si: 'Togli', pericolo: true })) return;
    try {
      await api.delete(`/calendario/${evento.id}/presenza${mio ? '' : `/${p.user_id}`}`);
      setLista(l => l.filter(x => x.user_id !== p.user_id));
      if (mio) setScelta({ ruolo_id: '', nota: '' });
      setErr(''); onCambio?.();
    } catch (er) { setErr(errore(er, 'Non tolta')); }
  };

  return (
    <div style={s.boxSerie}>
      <span style={s.etichetta}><Users size={13} style={{ verticalAlign: -2 }} /> Staff all'evento</span>
      {err && <div style={s.err}>{err}</div>}
      {lista.length === 0 && <span style={s.dim}>Nessuno ha ancora segnato la sua presenza.</span>}
      {lista.map(p => (
        <div key={p.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: p.ruolo.colore || '#64748B', flexShrink: 0 }} />
          <b>{p.user.nome}</b><span style={s.dim}>{p.ruolo.nome}{p.nota ? ` · ${p.nota}` : ''}</span>
          {(p.user_id === user.id || puoAltri) && <button type="button" style={{ ...s.icona, marginLeft: 'auto' }} title="Togli" onClick={() => togli(p)}><X size={15} /></button>}
        </div>
      ))}
      {aperto ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          <div style={s.opzioni}>
            {scegliibili.map(r => (
              <button type="button" key={r.id} title={r.descrizione || ''} aria-pressed={+scelta.ruolo_id === r.id}
                style={{ ...s.opzVis, ...(+scelta.ruolo_id === r.id ? s.opzOn : {}) }} onClick={() => setScelta(x => ({ ...x, ruolo_id: r.id }))}>
                <b><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 5, background: r.colore || '#64748B', marginRight: 6 }} />{r.nome}</b>
                {r.descrizione && <span style={s.opzSpiega}>{r.descrizione}</span>}
              </button>
            ))}
          </div>
          <input style={s.input} maxLength={300} value={scelta.nota} onChange={e => setScelta(x => ({ ...x, nota: e.target.value }))} placeholder="Nota (facoltativa), es. arrivo alle 10, solo la mattina" />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" style={s.btnPrimario} onClick={salva}><UserCheck size={14} /> {mia ? 'Aggiorna' : 'Ci sono'}</button>
            <button type="button" style={s.btn} onClick={() => { setAperto(false); setErr(''); }}>Annulla</button>
          </div>
        </div>
      ) : (
        <div><button type="button" style={s.btn} onClick={() => { setScelta({ ruolo_id: mia?.ruolo.id || '', nota: mia?.nota || '' }); setAperto(true); }}>
          <UserCheck size={14} /> {mia ? 'Cambia il mio ruolo' : 'Segna la mia presenza'}</button></div>
      )}
    </div>
  );
}

// ─── Finestra nuovo / modifica evento ──────────────────────────────────────────────────
export function FormEvento({ evento, data, luoghi, tags, onChiudi, onSalvato, onPresenza }) {
  const { user } = useAuth();
  const nuovo = !evento;
  const puoModificare = nuovo || puoFare(user, 'calendario', 'edit');
  const puoEliminare = !nuovo && puoFare(user, 'calendario', 'delete');
  const [f, setF] = useState(() => evento ? {
    titolo: evento.titolo, data: inizio(evento), data_fine: evento.data_fine ? fine(evento) : '',
    piu_giorni: !!evento.data_fine, tutto_giorno: !evento.ora_inizio,
    ora_inizio: evento.ora_inizio || '18:00', ora_fine: evento.ora_fine || '',
    luogo_id: evento.luogo?.id || '', campi: evento.campi || '', tags: evento.tags, visibile_a: evento.visibile_a, colore: evento.colore || '', note: evento.note || '',
    bozza: !!evento.bozza,
  } : {
    titolo: '', data: data || iso(new Date()), data_fine: '', piu_giorni: false, tutto_giorno: false,
    ora_inizio: '18:00', ora_fine: '20:00', luogo_id: '', campi: '', tags: [], visibile_a: ['STAFF'], colore: '', note: '', bozza: false,
  });
  const [ripeti, setRipeti] = useState(null); // { fino_al, giorni_settimana }
  const [serie, setSerie] = useState('solo');
  const [conta, setConta] = useState(null);
  const [nuovoTag, setNuovoTag] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (evento?.serie_id) api.get(`/calendario/${evento.id}/serie`).then(r => setConta(r.data)).catch(() => {});
  }, [evento]);

  const aggiungiTag = (t) => {
    const v = t.trim();
    if (v && !f.tags.some(x => x.toLowerCase() === v.toLowerCase())) set('tags', [...f.tags, v]);
    setNuovoTag('');
  };
  const giornoSett = (new Date(f.data + 'T12:00:00').getDay() + 6) % 7;
  const attivaRipeti = () => setRipeti(r => r ? null : { fino_al: '', giorni_settimana: [giornoSett] });
  const toggleGiorno = (i) => setRipeti(r => ({ ...r, giorni_settimana: r.giorni_settimana.includes(i) ? r.giorni_settimana.filter(x => x !== i) : [...r.giorni_settimana, i] }));

  // Anteprima quante date crea la ripetizione
  const nRipeti = useMemo(() => {
    if (!ripeti?.fino_al || !ripeti.giorni_settimana.length || ripeti.fino_al < f.data) return 0;
    let n = 0;
    for (let d = new Date(f.data + 'T12:00:00'), e = new Date(ripeti.fino_al + 'T12:00:00'); d <= e; d.setDate(d.getDate() + 1))
      if (ripeti.giorni_settimana.includes((d.getDay() + 6) % 7)) n++;
    return n;
  }, [ripeti, f.data]);

  const corpo = () => {
    const b = {
      titolo: f.titolo, luogo_id: f.luogo_id || null, campi: f.campi === '' ? null : +f.campi, tags: f.tags, visibile_a: f.visibile_a, colore: f.colore || null, note: f.note, bozza: f.bozza,
      ora_inizio: f.tutto_giorno ? null : f.ora_inizio || null, ora_fine: f.tutto_giorno ? null : f.ora_fine || null,
    };
    if (serie === 'solo') { b.data = f.data; b.data_fine = f.piu_giorni && !ripeti ? f.data_fine || null : null; }
    if (nuovo && ripeti) b.ripeti = ripeti;
    return b;
  };

  const salva = async (e) => {
    e.preventDefault();
    if (f.piu_giorni && !ripeti && !f.data_fine) { setErr('Indica la data di fine o togli "Più giorni"'); return; }
    if (!f.visibile_a.length) { setErr('Spunta almeno uno tra Staff, Utenti e Pubblico'); return; }
    if (ripeti && !nRipeti) { setErr('Scegli fino a quando ripetere e almeno un giorno della settimana'); return; }
    setSalvando(true); setErr('');
    try {
      if (nuovo) {
        const r = await api.post('/calendario', corpo());
        if (r.data.creati) await avvisa(`Creati ${r.data.creati} eventi.`);
      } else {
        await api.put(`/calendario/${evento.id}${evento.serie_id ? `?serie=${serie}` : ''}`, corpo());
      }
      onSalvato();
    } catch (er) { setErr(errore(er, 'Non salvato')); }
    finally { setSalvando(false); }
  };

  const elimina = async () => {
    const quanti = serie === 'tutti' ? conta?.tutti : serie === 'futuri' ? conta?.futuri : 1;
    const testo = quanti > 1 ? `Eliminare ${quanti} eventi della serie "${evento.titolo}"?` : `Eliminare "${evento.titolo}"?`;
    if (!await conferma(testo, { si: 'Elimina', pericolo: true })) return;
    try { await api.delete(`/calendario/${evento.id}${evento.serie_id ? `?serie=${serie}` : ''}`); onSalvato(); }
    catch (er) { setErr(errore(er, 'Non eliminato')); }
  };

  const bloccaDate = !nuovo && serie !== 'solo';
  return (
    <InFinestra attiva onChiudi={onChiudi} larghezza={640}>
      <form style={s.finestra} onSubmit={salva}>
        <div style={s.testaCol}>
          <div style={s.titoletto}>{nuovo ? 'Nuovo evento' : puoModificare ? 'Modifica evento' : 'Evento'}</div>
          <button type="button" style={s.icona} onClick={onChiudi} aria-label="Chiudi"><X size={18} /></button>
        </div>
        {err && <div style={s.err}>{err}</div>}
        {/* in cima: chi ha solo "vedi" apre l'evento soprattutto per segnarsi */}
        {!nuovo && <StaffEvento evento={evento} onCambio={onPresenza} />}
        <fieldset disabled={!puoModificare} style={s.fieldset}>
          <label style={s.campo}><span style={s.etichetta}>Titolo *</span>
            <input style={s.input} value={f.titolo} onChange={e => set('titolo', e.target.value)} required autoFocus={nuovo} placeholder="es. Torneo B-EASY, Allenamento U18, Festa di fine stagione" /></label>

          {evento?.serie_id && puoModificare && (
            <div style={s.boxSerie}>
              <span style={s.etichetta}>Questo evento si ripete — le modifiche valgono per</span>
              <div style={s.opzioni}>
                {[['solo', 'Solo questo'], ['futuri', `Da questo in poi${conta ? ` (${conta.futuri})` : ''}`], ['tutti', `Tutta la serie${conta ? ` (${conta.tutti})` : ''}`]].map(([k, l]) =>
                  <button type="button" key={k} style={{ ...s.opz, ...(serie === k ? s.opzOn : {}) }} onClick={() => setSerie(k)}>{l}</button>)}
              </div>
              {bloccaDate && <span style={s.dim}>Su più eventi insieme si cambiano i dettagli; ogni evento tiene il suo giorno.</span>}
            </div>
          )}

          <div style={s.campi}>
            <label style={s.campo}><span style={s.etichetta}>{f.piu_giorni && !ripeti ? 'Dal' : 'Giorno'} *</span>
              <input type="date" style={s.input} value={f.data} disabled={bloccaDate} onChange={e => set('data', e.target.value)} required /></label>
            {f.piu_giorni && !ripeti && (
              <label style={s.campo}><span style={s.etichetta}>Al *</span>
                <input type="date" style={s.input} value={f.data_fine} min={f.data} disabled={bloccaDate} onChange={e => set('data_fine', e.target.value)} /></label>
            )}
          </div>
          <div style={s.spunte}>
            {!ripeti && <label style={s.spunta}><input type="checkbox" checked={f.piu_giorni} disabled={bloccaDate} onChange={e => set('piu_giorni', e.target.checked)} /> Più giorni</label>}
            <label style={s.spunta}><input type="checkbox" checked={f.tutto_giorno} onChange={e => set('tutto_giorno', e.target.checked)} /> Tutto il giorno</label>
            {nuovo && !f.piu_giorni && <label style={s.spunta}><input type="checkbox" checked={!!ripeti} onChange={attivaRipeti} /> Si ripete</label>}
          </div>
          {!f.tutto_giorno && (
            <div style={s.campi}>
              <label style={s.campo}><span style={s.etichetta}>Dalle</span>
                <input type="time" step="900" style={s.input} value={f.ora_inizio} onChange={e => set('ora_inizio', e.target.value)} /></label>
              <label style={s.campo}><span style={s.etichetta}>Alle</span>
                <input type="time" step="900" style={s.input} value={f.ora_fine} onChange={e => set('ora_fine', e.target.value)} /></label>
            </div>
          )}

          {ripeti && (
            <div style={s.boxSerie}>
              <span style={s.etichetta}>Ripeti ogni settimana nei giorni</span>
              <div style={s.opzioni}>
                {GIORNI.map((g, i) => <button type="button" key={g} style={{ ...s.opz, ...(ripeti.giorni_settimana.includes(i) ? s.opzOn : {}) }} onClick={() => toggleGiorno(i)}>{g}</button>)}
              </div>
              <label style={{ ...s.campo, maxWidth: 220 }}><span style={s.etichetta}>Fino al *</span>
                <input type="date" style={s.input} min={f.data} value={ripeti.fino_al} onChange={e => setRipeti(r => ({ ...r, fino_al: e.target.value }))} /></label>
              {nRipeti > 0 && <span style={s.dim}>Verranno creati <b>{nRipeti}</b> eventi, modificabili uno per uno o tutti insieme.</span>}
            </div>
          )}

          <div style={s.campi}>
            <label style={s.campo}><span style={s.etichetta}>Sede</span>
              <select style={s.input} value={f.luogo_id} onChange={e => set('luogo_id', e.target.value)}>
                <option value="">— nessuna —</option>
                {luoghi.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
              </select></label>
            <label style={s.campo}><span style={s.etichetta}>Campi occupati</span>
              <input type="number" min="1" max="50" style={s.input} value={f.campi} onChange={e => set('campi', e.target.value)} placeholder="es. 3" /></label>
          </div>

          <div style={s.campo}><span style={s.etichetta}>Chi lo vede (uno o più)</span>
            <div style={s.opzioni}>
              {VISIBILITA.map(([k, l, sp]) => {
                const on = f.visibile_a.includes(k);
                return (
                  <button type="button" key={k} aria-pressed={on} style={{ ...s.opzVis, ...(on ? s.opzOn : {}) }}
                    onClick={() => set('visibile_a', on ? f.visibile_a.filter(x => x !== k) : VISIBILITA.map(v => v[0]).filter(x => x === k || f.visibile_a.includes(x)))}>
                    <b><input type="checkbox" readOnly checked={on} tabIndex={-1} style={{ margin: '0 6px 0 0', verticalAlign: -2 }} />{l}</b><span style={s.opzSpiega}>{sp}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <label style={{ ...s.boxSerie, ...(f.bozza ? s.boxBozza : {}), flexDirection: 'row', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.bozza} onChange={e => set('bozza', e.target.checked)} style={{ marginTop: 3 }} />
            <span><b>Bozza — da confermare</b><br /><span style={s.dim}>La vede solo lo staff, con l'etichetta BOZZA. Non compare nei link condivisi (es. alla struttura) né agli utenti, anche se qui sopra è spuntato Pubblico o Utenti. Togli la spunta quando è confermato.</span></span>
          </label>

          <div style={s.campo}><span style={s.etichetta}>Tag</span>
            <div style={s.tagBox}>
              {f.tags.map(t => <span key={t} style={{ ...s.tag, ...s.tagArancio, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{t}
                {puoModificare && <X size={12} style={{ cursor: 'pointer' }} onClick={() => set('tags', f.tags.filter(x => x !== t))} />}</span>)}
              {puoModificare && <input style={s.tagInput} list="tag-calendario" value={nuovoTag} placeholder={f.tags.length ? 'altro tag…' : 'es. Torneo, Corsi, Camp — Invio per aggiungere'}
                onChange={e => setNuovoTag(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); aggiungiTag(nuovoTag); } }}
                onBlur={() => nuovoTag && aggiungiTag(nuovoTag)} />}
              <datalist id="tag-calendario">{tags.filter(t => !f.tags.includes(t)).map(t => <option key={t} value={t} />)}</datalist>
            </div>
          </div>

          <div style={s.campo}><span style={s.etichetta}>Colore</span>
            <div style={s.opzioni}>
              <button type="button" style={{ ...s.opz, ...(!f.colore ? s.opzOn : {}) }} onClick={() => set('colore', '')}>Come la sede</button>
              {PALETTE.map(c => <button type="button" key={c} aria-label={c} onClick={() => set('colore', c)}
                style={{ ...s.pallino, background: c, outline: f.colore === c ? '2px solid var(--text)' : 'none' }} />)}
            </div>
          </div>

          <label style={s.campo}><span style={s.etichetta}>Note</span>
            <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }} value={f.note} onChange={e => set('note', e.target.value)} placeholder="Dettagli, campi occupati, referente…" /></label>
        </fieldset>
        {!nuovo && <div style={s.dim}>Creato da {evento.createdBy?.nome}</div>}
        <div style={s.piede}>
          {puoEliminare && <button type="button" style={{ ...s.btn, color: '#C0392B', marginRight: 'auto' }} onClick={elimina}><Trash2 size={14} /> Elimina</button>}
          <button type="button" style={s.btn} onClick={onChiudi}>{puoModificare ? 'Annulla' : 'Chiudi'}</button>
          {puoModificare && <button type="submit" style={s.btnPrimario} disabled={salvando}>{salvando ? 'Salvo…' : nuovo && nRipeti > 1 ? `Crea ${nRipeti} eventi` : 'Salva'}</button>}
        </div>
      </form>
    </InFinestra>
  );
}

// ─── Ruoli staff (solo ROOT, 27/10): l'elenco tra cui lo staff sceglie quando segna la presenza a un evento ────
function RuoliStaff() {
  const [lista, setLista] = useState(null);
  const [edit, setEdit] = useState(null); // { id?, nome, descrizione, colore, ordine }
  const [err, setErr] = useState('');
  const carica = () => api.get('/calendario/ruoli-staff').then(r => setLista(r.data)).catch(e => setErr(errore(e, 'Ruoli non caricati')));
  useEffect(() => { carica(); }, []);

  const salva = async (e) => {
    e.preventDefault();
    try {
      const { id, ...b } = edit;
      b.ordine = b.ordine === '' ? 0 : +b.ordine;
      if (id) await api.put(`/calendario/ruoli-staff/${id}`, b); else await api.post('/calendario/ruoli-staff', b);
      setEdit(null); setErr(''); carica();
    } catch (er) { setErr(errore(er, 'Non salvato')); }
  };
  const attiva = async (r) => {
    try { await api.put(`/calendario/ruoli-staff/${r.id}`, { attivo: !r.attivo }); carica(); }
    catch (er) { setErr(errore(er, 'Non salvato')); }
  };
  const elimina = async (r) => {
    if (!await conferma(`Eliminare il ruolo "${r.nome}"?`, { si: 'Elimina', pericolo: true })) return;
    try { await api.delete(`/calendario/ruoli-staff/${r.id}`); setErr(''); carica(); }
    catch (er) { setErr(errore(er, 'Non eliminato')); }
  };

  if (!lista) return <div style={s.vuoto}>Carico…</div>;
  return (
    <div style={s.boxElenco}>
      <div style={s.testaCol}>
        <div style={s.dim}>I ruoli tra cui lo staff sceglie quando segna la sua presenza a un evento (finestra dell'evento → Segna la mia presenza). Li gestisci solo tu. Un ruolo già usato non si elimina: si disattiva, e chi l'aveva lo tiene.</div>
        <button style={s.btnPrimario} onClick={() => setEdit({ nome: '', descrizione: '', colore: PALETTE[lista.length % PALETTE.length], ordine: (lista.length + 1) })}><Plus size={15} /> Nuovo ruolo</button>
      </div>
      {err && <div style={s.err}>{err}</div>}
      {lista.map(r => (
        <div key={r.id} style={{ ...s.rigaSede, opacity: r.attivo ? 1 : 0.55 }}>
          <span style={{ ...s.pallino, background: r.colore || '#64748B', cursor: 'default' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700 }}>{r.nome} {!r.attivo && <span style={{ ...s.tag, ...s.tagGrigio }}>non attivo</span>}</div>
            <div style={s.dim}>{r.descrizione || 'nessuna spiegazione'} · {r.usato ? `usato ${r.usato} ${r.usato === 1 ? 'volta' : 'volte'}` : 'mai usato'}</div>
          </div>
          <button style={s.icona} title="Modifica" onClick={() => setEdit({ id: r.id, nome: r.nome, descrizione: r.descrizione || '', colore: r.colore || PALETTE[0], ordine: r.ordine })}><Pencil size={15} /></button>
          <button style={s.btn} onClick={() => attiva(r)}>{r.attivo ? 'Disattiva' : 'Riattiva'}</button>
          {!r.usato && <button style={s.icona} title="Elimina" onClick={() => elimina(r)}><Trash2 size={15} /></button>}
        </div>
      ))}
      {edit && (
        <InFinestra attiva onChiudi={() => setEdit(null)} larghezza={480}>
          <form style={s.finestra} onSubmit={salva}>
            <div style={s.titoletto}>{edit.id ? 'Modifica ruolo' : 'Nuovo ruolo'}</div>
            <label style={s.campo}><span style={s.etichetta}>Nome *</span>
              <input style={s.input} maxLength={60} value={edit.nome} onChange={e => setEdit(p => ({ ...p, nome: e.target.value }))} required autoFocus placeholder="es. Gestione completa dell'evento" /></label>
            <label style={s.campo}><span style={s.etichetta}>Spiegazione (la vede lo staff quando sceglie)</span>
              <input style={s.input} maxLength={200} value={edit.descrizione} onChange={e => setEdit(p => ({ ...p, descrizione: e.target.value }))} placeholder="es. Dà una mano durante l'evento" /></label>
            <label style={{ ...s.campo, maxWidth: 160 }}><span style={s.etichetta}>Ordine nell'elenco</span>
              <input type="number" min="0" max="999" style={s.input} value={edit.ordine} onChange={e => setEdit(p => ({ ...p, ordine: e.target.value }))} /></label>
            <div style={s.campo}><span style={s.etichetta}>Colore</span>
              <div style={s.opzioni}>{PALETTE.map(c => <button type="button" key={c} aria-label={c} onClick={() => setEdit(p => ({ ...p, colore: c }))}
                style={{ ...s.pallino, background: c, outline: edit.colore === c ? '2px solid var(--text)' : 'none' }} />)}</div>
            </div>
            <div style={s.piede}>
              <button type="button" style={s.btn} onClick={() => setEdit(null)}>Annulla</button>
              <button type="submit" style={s.btnPrimario}>Salva</button>
            </div>
          </form>
        </InFinestra>
      )}
    </div>
  );
}

// ─── Sedi ──────────────────────────────────────────────────────────────────────────────
function Sedi() {
  const { user } = useAuth();
  const puo = puoFare(user, 'calendario', 'edit');
  const [lista, setLista] = useState(null);
  const [edit, setEdit] = useState(null); // { id?, nome, indirizzo, colore }
  const [err, setErr] = useState('');
  const carica = () => api.get('/calendario/luoghi').then(r => setLista(r.data)).catch(e => setErr(errore(e, 'Sedi non caricate')));
  useEffect(() => { carica(); }, []);

  const salva = async (e) => {
    e.preventDefault();
    try {
      const { id, ...b } = edit;
      if (id) await api.put(`/calendario/luoghi/${id}`, b); else await api.post('/calendario/luoghi', b);
      setEdit(null); setErr(''); carica();
    } catch (er) { setErr(errore(er, 'Non salvata')); }
  };
  const attiva = async (l) => {
    try { await api.put(`/calendario/luoghi/${l.id}`, { attivo: !l.attivo }); carica(); }
    catch (er) { setErr(errore(er, 'Non salvata')); }
  };

  if (!lista) return <div style={s.vuoto}>Carico…</div>;
  return (
    <div style={s.boxElenco}>
      <div style={s.testaCol}>
        <div style={s.dim}>Le sedi in cui si svolgono gli eventi. Il colore della sede colora i suoi eventi nel calendario. Una sede non più usata si disattiva (gli eventi passati la tengono).</div>
        {puo && <button style={s.btnPrimario} onClick={() => setEdit({ nome: '', indirizzo: '', colore: PALETTE[lista.length % PALETTE.length] })}><Plus size={15} /> Nuova sede</button>}
      </div>
      {err && <div style={s.err}>{err}</div>}
      {lista.map(l => (
        <div key={l.id} style={{ ...s.rigaSede, opacity: l.attivo ? 1 : 0.55 }}>
          <span style={{ ...s.pallino, background: l.colore, cursor: 'default' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700 }}>{l.nome} {!l.attivo && <span style={{ ...s.tag, ...s.tagGrigio }}>non attiva</span>}</div>
            {l.indirizzo && <div style={s.dim}>{l.indirizzo}</div>}
          </div>
          {puo && <>
            <button style={s.icona} title="Modifica" onClick={() => setEdit({ id: l.id, nome: l.nome, indirizzo: l.indirizzo || '', colore: l.colore })}><Pencil size={15} /></button>
            <button style={s.btn} onClick={() => attiva(l)}>{l.attivo ? 'Disattiva' : 'Riattiva'}</button>
          </>}
        </div>
      ))}
      {edit && (
        <InFinestra attiva onChiudi={() => setEdit(null)} larghezza={480}>
          <form style={s.finestra} onSubmit={salva}>
            <div style={s.titoletto}>{edit.id ? 'Modifica sede' : 'Nuova sede'}</div>
            <label style={s.campo}><span style={s.etichetta}>Nome *</span>
              <input style={s.input} value={edit.nome} onChange={e => setEdit(p => ({ ...p, nome: e.target.value }))} required autoFocus /></label>
            <label style={s.campo}><span style={s.etichetta}>Indirizzo</span>
              <input style={s.input} value={edit.indirizzo} onChange={e => setEdit(p => ({ ...p, indirizzo: e.target.value }))} /></label>
            <div style={s.campo}><span style={s.etichetta}>Colore</span>
              <div style={s.opzioni}>{PALETTE.map(c => <button type="button" key={c} aria-label={c} onClick={() => setEdit(p => ({ ...p, colore: c }))}
                style={{ ...s.pallino, background: c, outline: edit.colore === c ? '2px solid var(--text)' : 'none' }} />)}</div>
            </div>
            <div style={s.piede}>
              <button type="button" style={s.btn} onClick={() => setEdit(null)}>Annulla</button>
              <button type="submit" style={s.btnPrimario}>Salva</button>
            </div>
          </form>
        </InFinestra>
      )}
    </div>
  );
}

// ─── Link condivisi ────────────────────────────────────────────────────────────────────
// Ogni link ha un indirizzo segreto: pagina in sola lettura senza login. I destinatari (26/10) hanno ognuno il suo
// link personale: quando lo decide lo staff, INVIA AGGIORNAMENTO manda a ognuno un'email con le modifiche da quando
// ha visto l'ultima volta; lui tocca "Ho visto" e qui (e nell'avviso a video) si vede chi ha visto e quando.
const urlPagina = (t) => `${window.location.origin}/calendario-pubblico/${t}`;

function LinkCondivisi() {
  const { user } = useAuth();
  const puoEliminare = puoFare(user, 'calendario', 'delete');
  const [lista, setLista] = useState(null);
  const [luoghi, setLuoghi] = useState([]);
  const [tags, setTags] = useState([]);
  const [edit, setEdit] = useState(null);
  const [dest, setDest] = useState(null);
  const [invio, setInvio] = useState(null);
  const [copiato, setCopiato] = useState('');
  const [err, setErr] = useState('');
  const carica = () => api.get('/calendario/condivisi').then(r => setLista(r.data)).catch(e => setErr(errore(e, 'Link non caricati')));
  useEffect(() => {
    carica();
    api.get('/calendario/luoghi').then(r => setLuoghi(r.data)).catch(() => {});
    api.get('/calendario/tags').then(r => setTags(r.data)).catch(() => {});
  }, []);

  const copia = async (testo, chiave) => {
    try { await navigator.clipboard.writeText(testo); setCopiato(chiave); setTimeout(() => setCopiato(''), 2000); }
    catch { await avvisa('Copia questo indirizzo:\n' + testo); }
  };
  const salva = async (e) => {
    e.preventDefault();
    try {
      const { id, ...b } = edit;
      if (id) await api.put(`/calendario/condivisi/${id}`, b); else await api.post('/calendario/condivisi', b);
      setEdit(null); setErr(''); carica();
    } catch (er) { setErr(errore(er, 'Non salvato')); }
  };
  const attiva = async (l) => {
    try { await api.put(`/calendario/condivisi/${l.id}`, { attivo: !l.attivo }); carica(); }
    catch (er) { setErr(errore(er, 'Non salvato')); }
  };
  const elimina = async (l) => {
    if (!await conferma(`Eliminare il link "${l.nome}"? Chi lo usa non vedrà più il calendario e l'indirizzo non si può recuperare.`, { si: 'Elimina', pericolo: true })) return;
    try { await api.delete(`/calendario/condivisi/${l.id}`); carica(); }
    catch (er) { setErr(errore(er, 'Non eliminato')); }
  };
  const salvaDest = async (e) => {
    e.preventDefault();
    try {
      const { id, link_id, ...b } = dest;
      if (id) await api.put(`/calendario/destinatari/${id}`, b); else await api.post(`/calendario/condivisi/${link_id}/destinatari`, b);
      setDest(null); setErr(''); carica();
    } catch (er) { setErr(errore(er, 'Non salvato')); }
  };
  const attivaDest = async (d) => {
    try { await api.put(`/calendario/destinatari/${d.id}`, { attivo: !d.attivo }); carica(); }
    catch (er) { setErr(errore(er, 'Non salvato')); }
  };
  const eliminaDest = async (d) => {
    if (!await conferma(`Togliere ${d.nome} dai destinatari? Il suo link personale smette di funzionare.`, { si: 'Togli', pericolo: true })) return;
    try { await api.delete(`/calendario/destinatari/${d.id}`); carica(); }
    catch (er) { setErr(errore(er, 'Non eliminato')); }
  };
  const nomeSede = (id) => luoghi.find(l => l.id === id)?.nome || '?';
  const toggle = (k, v) => setEdit(p => ({ ...p, [k]: p[k].includes(v) ? p[k].filter(x => x !== v) : [...p[k], v] }));

  if (!lista) return <div style={s.vuoto}>{err || 'Carico…'}</div>;
  return (
    <div style={s.boxElenco}>
      <div style={s.testaCol}>
        <div style={{ ...s.dim, maxWidth: 640 }}>Un link fa vedere il calendario a chi non ha un accesso, <b>senza password e in sola lettura</b>: una pagina web sempre aggiornata. Chi ha il link vede gli eventi scelti qui: per esempio solo quelli di Robbiate, con giorni, orari e campi occupati. Aggiungi i <b>destinatari</b> (es. il gestore della struttura): ognuno ha il suo link personale e, quando lo decidi tu, riceve un'email con le modifiche. Quando tocca «Ho visto» ti compare un avviso. Disattiva il link quando non serve più.</div>
        <button style={s.btnPrimario} onClick={() => setEdit({ nome: '', luogo_ids: [], tags: [], solo_pubblici: true, mostra_dettagli: true })}><Plus size={15} /> Nuovo link</button>
      </div>
      {err && <div style={s.err}>{err}</div>}
      {lista.length === 0 && <div style={s.dim}>Nessun link ancora.</div>}
      {lista.map(l => (
        <div key={l.id} style={{ ...s.rigaLink, opacity: l.attivo ? 1 : 0.55 }}>
          <div style={s.testaCol}>
            <div style={{ fontWeight: 800 }}>{l.nome} {!l.attivo && <span style={{ ...s.tag, ...s.tagGrigio }}>disattivato</span>}</div>
            <div style={s.opzioni}>
              <button style={s.icona} title="Modifica" onClick={() => setEdit({ id: l.id, nome: l.nome, luogo_ids: l.luogo_ids, tags: l.tags, solo_pubblici: l.solo_pubblici, mostra_dettagli: l.mostra_dettagli })}><Pencil size={15} /></button>
              <button style={s.btn} onClick={() => attiva(l)}>{l.attivo ? 'Disattiva' : 'Riattiva'}</button>
              {puoEliminare && <button style={s.icona} title="Elimina" onClick={() => elimina(l)}><Trash2 size={15} /></button>}
            </div>
          </div>
          <div style={s.rigaInfo}>
            <span style={s.info}><MapPin size={13} /> {l.luogo_ids.length ? l.luogo_ids.map(nomeSede).join(', ') : 'Tutte le sedi'}</span>
            <span style={s.info}>Tag: {l.tags.length ? l.tags.join(', ') : 'tutti'}</span>
            <span style={{ ...s.tag, ...(l.solo_pubblici ? s.tagVerde : s.tagArancio) }}>{l.solo_pubblici ? 'Solo eventi Pubblici' : 'Tutti gli eventi del filtro'}</span>
            <span style={{ ...s.tag, ...s.tagGrigio }}>{l.mostra_dettagli ? 'Con titolo e dettagli' : 'Solo "Campo occupato"'}</span>
          </div>
          <Destinatari l={l} copia={copia} copiato={copiato} onNuovo={() => setDest({ link_id: l.id, nome: '', email: '' })}
            onModifica={d => setDest({ id: d.id, nome: d.nome, email: d.email })} onAttiva={attivaDest} onElimina={eliminaDest}
            onInvia={() => setInvio(l)} />
          {l.registro?.length > 0 && (
            <details style={s.dim}><summary style={{ cursor: 'pointer' }}>Storico invii e prese visione</summary>
              <div style={s.storico}>{l.registro.map(r => <RigaStorico key={r.id} r={r} />)}</div>
            </details>
          )}
          {l.attivo && (
            <div style={s.opzioni}>
              <a style={s.btn} href={urlPagina(l.token)} target="_blank" rel="noreferrer">Apri pagina</a>
              <button style={s.btn} onClick={() => copia(urlPagina(l.token), l.id + 'p')}>{copiato === l.id + 'p' ? '✓ Copiato' : 'Copia link generico'}</button>
            </div>
          )}
        </div>
      ))}
      {edit && (
        <InFinestra attiva onChiudi={() => setEdit(null)} larghezza={560}>
          <form style={s.finestra} onSubmit={salva}>
            <div style={s.testaCol}>
              <div style={s.titoletto}>{edit.id ? 'Modifica link' : 'Nuovo link condiviso'}</div>
              <button type="button" style={s.icona} onClick={() => setEdit(null)} aria-label="Chiudi"><X size={18} /></button>
            </div>
            {err && <div style={s.err}>{err}</div>}
            <label style={s.campo}><span style={s.etichetta}>Nome *</span>
              <input style={s.input} value={edit.nome} onChange={e => setEdit(p => ({ ...p, nome: e.target.value }))} required autoFocus placeholder="es. Struttura Robbiate, Calendario pubblico" /></label>
            <div style={s.campo}><span style={s.etichetta}>Sedi (nessuna = tutte)</span>
              <div style={s.opzioni}>{luoghi.map(l => <button type="button" key={l.id} style={{ ...s.opz, ...(edit.luogo_ids.includes(l.id) ? s.opzOn : {}) }} onClick={() => toggle('luogo_ids', l.id)}>{l.nome}</button>)}</div>
            </div>
            {tags.length > 0 && (
              <div style={s.campo}><span style={s.etichetta}>Tag (nessuno = tutti; più tag = basta averne uno)</span>
                <div style={s.opzioni}>{tags.map(t => <button type="button" key={t} style={{ ...s.opz, ...(edit.tags.includes(t) ? s.opzOn : {}) }} onClick={() => toggle('tags', t)}>{t}</button>)}</div>
              </div>
            )}
            <div style={s.campo}><span style={s.etichetta}>Quali eventi</span>
              <div style={s.opzioni}>
                {[[true, 'Solo quelli Pubblici', 'Gli eventi con la spunta Pubblico'], [false, 'Tutti quelli del filtro', 'Anche quelli solo staff: es. per una struttura che deve sapere quando occupiamo il campo']].map(([v, l, sp]) =>
                  <button type="button" key={l} style={{ ...s.opzVis, ...(edit.solo_pubblici === v ? s.opzOn : {}) }} onClick={() => setEdit(p => ({ ...p, solo_pubblici: v }))}><b>{l}</b><span style={s.opzSpiega}>{sp}</span></button>)}
              </div>
            </div>
            <div style={s.campo}><span style={s.etichetta}>Cosa si vede</span>
              <div style={s.opzioni}>
                {[[true, 'Titolo e dettagli', 'Titolo, orario, sede, tag; le note solo degli eventi Pubblici'], [false, 'Solo "Campo occupato"', 'Giorno, orario e sede, senza dire cosa facciamo']].map(([v, l, sp]) =>
                  <button type="button" key={l} style={{ ...s.opzVis, ...(edit.mostra_dettagli === v ? s.opzOn : {}) }} onClick={() => setEdit(p => ({ ...p, mostra_dettagli: v }))}><b>{l}</b><span style={s.opzSpiega}>{sp}</span></button>)}
              </div>
            </div>
            <div style={s.piede}>
              <button type="button" style={s.btn} onClick={() => setEdit(null)}>Annulla</button>
              <button type="submit" style={s.btnPrimario}>Salva</button>
            </div>
          </form>
        </InFinestra>
      )}
      {dest && (
        <InFinestra attiva onChiudi={() => setDest(null)} larghezza={460}>
          <form style={s.finestra} onSubmit={salvaDest}>
            <div style={s.testaCol}>
              <div style={s.titoletto}>{dest.id ? 'Modifica destinatario' : 'Nuovo destinatario'}</div>
              <button type="button" style={s.icona} onClick={() => setDest(null)} aria-label="Chiudi"><X size={18} /></button>
            </div>
            {err && <div style={s.err}>{err}</div>}
            <label style={s.campo}><span style={s.etichetta}>Nome *</span>
              <input style={s.input} value={dest.nome} onChange={e => setDest(p => ({ ...p, nome: e.target.value }))} required autoFocus placeholder="es. Mario Rossi – Comune di Robbiate" /></label>
            <label style={s.campo}><span style={s.etichetta}>Email *</span>
              <input style={s.input} type="email" value={dest.email} onChange={e => setDest(p => ({ ...p, email: e.target.value }))} required /></label>
            {!dest.id && <div style={s.dim}>Non parte nessuna email adesso: la mandi tu con INVIA AGGIORNAMENTO quando vuoi.</div>}
            <div style={s.piede}>
              <button type="button" style={s.btn} onClick={() => setDest(null)}>Annulla</button>
              <button type="submit" style={s.btnPrimario}>Salva</button>
            </div>
          </form>
        </InFinestra>
      )}
      {invio && <InviaAggiornamento l={invio} onChiudi={() => setInvio(null)} onFatto={carica} />}
    </div>
  );
}

const nMod = (n) => `${n} ${n === 1 ? 'modifica' : 'modifiche'}`;

// Destinatari di un link: stato di ognuno (mai mandato / email mandata / visto / modifiche non viste)
function Destinatari({ l, copia, copiato, onNuovo, onModifica, onAttiva, onElimina, onInvia }) {
  const attivi = l.destinatari.filter(d => d.attivo);
  const daVedere = attivi.filter(d => !d.visto_il || d.modifiche.length).length;
  return (
    <div style={s.boxDest}>
      <div style={s.testaCol}>
        <span style={s.etichetta}>Destinatari</span>
        <div style={s.opzioni}>
          <button style={s.btn} onClick={onNuovo}><Plus size={14} /> Aggiungi</button>
          {l.attivo && attivi.length > 0 && <button style={s.btnPrimario} onClick={onInvia}><Send size={14} /> Invia aggiornamento</button>}
        </div>
      </div>
      {l.destinatari.length === 0 && <div style={s.dim}>Nessuno: aggiungi chi deve ricevere le modifiche via email.</div>}
      {l.destinatari.map(d => {
        const stato = !d.visto_il ? (d.inviato_il ? 'attesa' : 'mai') : d.modifiche.length ? 'attesa' : 'ok';
        return (
          <div key={d.id} style={{ ...s.rigaDest, opacity: d.attivo ? 1 : 0.55 }}>
            <div style={{ minWidth: 0, flex: '1 1 220px' }}>
              <div style={{ fontWeight: 700 }}>{d.nome} {!d.attivo && <span style={{ ...s.tag, ...s.tagGrigio }}>sospeso</span>}</div>
              <div style={s.dim}><Mail size={12} style={{ verticalAlign: '-2px' }} /> {d.email}</div>
            </div>
            <div style={{ ...s.statoVisione, ...(stato === 'ok' ? s.visioneOk : stato === 'attesa' ? s.visioneAttesa : s.visioneMai), flex: '1 1 240px' }}>
              {stato === 'ok' ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
              <span>{!d.visto_il
                ? (d.inviato_il ? <>Email mandata {quando(d.inviato_il)} · <b>non ancora visto</b></> : 'Ancora nessuna email mandata')
                : d.modifiche.length
                  ? <><b>{nMod(d.modifiche.length)} non ancora {d.modifiche.length === 1 ? 'vista' : 'viste'}</b> · visto l'ultima volta {quando(d.visto_il)}{d.inviato_il && d.inviato_il > d.visto_il ? ` · email mandata ${quando(d.inviato_il)}` : ''}</>
                  : <>Tutto visto · {quando(d.visto_il)}</>}</span>
            </div>
            <div style={s.opzioni}>
              <button style={s.icona} title="Copia il suo link personale" onClick={() => copia(urlPagina(d.token), 'd' + d.id)}>{copiato === 'd' + d.id ? '✓' : <Link2 size={15} />}</button>
              <button style={s.icona} title="Modifica" onClick={() => onModifica(d)}><Pencil size={15} /></button>
              <button style={s.btn} onClick={() => onAttiva(d)}>{d.attivo ? 'Sospendi' : 'Riattiva'}</button>
              <button style={s.icona} title="Togli" onClick={() => onElimina(d)}><Trash2 size={15} /></button>
            </div>
          </div>
        );
      })}
      {l.attivo && daVedere > 0 && <div style={s.dim}>{daVedere === 1 ? '1 destinatario non ha' : `${daVedere} destinatari non hanno`} ancora visto la versione attuale.</div>}
    </div>
  );
}

function RigaStorico({ r }) {
  const testo = r.tipo === 'VISTO' ? <><b>{r.nome}</b> ha visto{r.modifiche ? ` (${nMod(r.modifiche)})` : ''}</>
    : r.tipo === 'INVIO' ? <>Email a <b>{r.nome}</b>{r.modifiche ? ` con ${nMod(r.modifiche)}` : ''}{r.utente_nome ? ` · da ${r.utente_nome}` : ''}</>
    : <>Email a <b>{r.nome}</b> NON partita{r.messaggio ? `: ${r.messaggio}` : ''}</>;
  return <div style={{ ...(r.tipo === 'ERRORE' ? { color: '#9B1C1C' } : {}) }}>{quando(r.created_at)} · {testo}</div>;
}

// Finestra INVIA AGGIORNAMENTO: chi riceve, cosa riceve (le sue modifiche), messaggio facoltativo
function InviaAggiornamento({ l, onChiudi, onFatto }) {
  const attivi = l.destinatari.filter(d => d.attivo);
  const [scelti, setScelti] = useState(() => attivi.filter(d => !d.visto_il || d.modifiche.length).map(d => d.id));
  const [messaggio, setMessaggio] = useState('');
  const [stato, setStato] = useState('');
  const [esito, setEsito] = useState(null);
  const [err, setErr] = useState('');
  const toggle = (id) => setScelti(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const invia = async () => {
    setStato('invio'); setErr('');
    try { const r = await api.post(`/calendario/condivisi/${l.id}/invia`, { destinatari: scelti, messaggio }); setEsito(r.data); onFatto(); }
    catch (er) { setErr(errore(er, 'Email non mandate')); }
    finally { setStato(''); }
  };
  return (
    <InFinestra attiva onChiudi={onChiudi} larghezza={560}>
      <div style={s.finestra}>
        <div style={s.testaCol}>
          <div style={s.titoletto}>Invia aggiornamento · {l.nome}</div>
          <button type="button" style={s.icona} onClick={onChiudi} aria-label="Chiudi"><X size={18} /></button>
        </div>
        {esito ? (
          <>
            {esito.esiti.map(x => <div key={x.nome} style={{ ...s.statoVisione, ...(x.ok ? s.visioneOk : s.visioneAttesa) }}>{x.ok ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}<span><b>{x.nome}</b>: {x.ok ? 'email mandata' : `non partita (${x.errore || 'errore'})`}</span></div>)}
            <div style={s.dim}>Quando toccano «Ho visto» ti compare un avviso a video.</div>
            <div style={s.piede}><button style={s.btnPrimario} onClick={onChiudi}>Chiudi</button></div>
          </>
        ) : (
          <>
            <div style={s.dim}>Ognuno riceve le modifiche da quando ha visto l'ultima volta e il suo link personale. Chi non ha mai visto riceve il link al calendario.</div>
            {err && <div style={s.err}>{err}</div>}
            {attivi.map(d => (
              <div key={d.id} style={s.rigaInvio}>
                <label style={s.spunta}>
                  <input type="checkbox" checked={scelti.includes(d.id)} onChange={() => toggle(d.id)} />
                  <span><b>{d.nome}</b> · {!d.visto_il ? 'prima email: link al calendario' : d.modifiche.length ? nMod(d.modifiche.length) : 'nessuna modifica (solo promemoria)'}</span>
                </label>
                {d.modifiche.length > 0 && (
                  <details style={s.dim}><summary style={{ cursor: 'pointer' }}>Cosa riceve</summary>
                    {d.modifiche.map(m => <div key={m.tipo + m.id}><b>{m.tipo === 'NUOVO' ? 'Nuovo' : m.tipo === 'ANNULLATO' ? 'Annullato' : 'Modificato'}</b> · {giornoCorto(m.data)} · {m.titolo}{m.cambi ? ' — ' + m.cambi.map(c => c.campo).join(', ') : ''}</div>)}
                  </details>
                )}
              </div>
            ))}
            <label style={s.campo}><span style={s.etichetta}>Messaggio (facoltativo, va in cima all'email)</span>
              <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }} value={messaggio} onChange={e => setMessaggio(e.target.value)} maxLength={1000} placeholder="es. Abbiamo spostato l'allenamento di sabato per il torneo" /></label>
            <div style={s.piede}>
              <button type="button" style={s.btn} onClick={onChiudi}>Annulla</button>
              <button style={s.btnPrimario} disabled={!scelti.length || stato === 'invio'} onClick={invia}><Send size={14} /> {stato === 'invio' ? 'Mando…' : scelti.length === 1 ? 'Manda 1 email' : `Manda ${scelti.length} email`}</button>
            </div>
          </>
        )}
      </div>
    </InFinestra>
  );
}

const s = {
  pagina: { padding: '4px 0 40px', color: 'var(--text)' },
  titolo: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight: 800, letterSpacing: '2px', margin: '2px 0 6px' },
  sub: { color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 14, maxWidth: 820 },
  schede: { display: 'inline-flex', gap: 4, background: 'var(--bg-input)', padding: 4, borderRadius: 12, marginBottom: 16, flexWrap: 'wrap' },
  schedeMini: { display: 'inline-flex', gap: 4, background: 'var(--bg-input)', padding: 4, borderRadius: 12 },
  scheda: { border: 'none', background: 'transparent', padding: '8px 14px', borderRadius: 9, fontWeight: 600, fontSize: 14, color: 'var(--text-muted)', cursor: 'pointer' },
  schedaOn: { background: 'var(--bg-card)', color: 'var(--text)', fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,.08)' },
  barra: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 },
  nav: { display: 'flex', alignItems: 'center', gap: 4 },
  mese: { fontWeight: 800, fontSize: 18, minWidth: 150, textAlign: 'center' },
  filtri: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 },
  select: { border: '1px solid var(--border2)', borderRadius: 12, padding: '8px 10px', fontSize: 14, color: 'var(--text)', background: 'var(--bg-card)', minHeight: 38, maxWidth: '100%' },
  colonne: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  boxGriglia: { flex: '1 1 auto', minWidth: 0, width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 10, boxSizing: 'border-box' },
  griglia7: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 },
  testaGiorno: { textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--text-muted)', textTransform: 'uppercase', padding: '4px 0 6px' },
  cella: { border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg)', padding: 4, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'stretch', cursor: 'pointer', textAlign: 'left', minWidth: 0, overflow: 'hidden', color: 'var(--text)' },
  cellaSel: { borderColor: '#C24B08', boxShadow: '0 0 0 1px #C24B08 inset' },
  numero: { fontSize: 12, fontWeight: 700, alignSelf: 'flex-start', minWidth: 22, height: 22, lineHeight: '22px', textAlign: 'center', borderRadius: 11 },
  numeroOggi: { background: '#C24B08', color: '#fff' },
  chip: { fontSize: 11, lineHeight: 1.3, borderLeft: '3px solid', borderRadius: 4, padding: '1px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  altri: { fontSize: 11, color: 'var(--text-muted)', paddingLeft: 4 },
  puntini: { display: 'flex', gap: 3, flexWrap: 'wrap', paddingLeft: 2 },
  puntino: { width: 7, height: 7, borderRadius: 4 },
  boxGiorno: { flex: '0 0 320px', maxWidth: 360, width: '100%', boxSizing: 'border-box', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  boxElenco: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  gruppoGiorno: { display: 'flex', flexDirection: 'column', gap: 6 },
  testaCol: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  titoletto: { fontWeight: 800, fontSize: 15 },
  rigaEv: { border: '1px solid var(--border)', borderLeft: '4px solid', borderRadius: 12, background: 'var(--bg)', padding: '9px 12px', textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4, color: 'var(--text)', width: '100%' },
  rigaTitolo: { fontWeight: 700, fontSize: 14 },
  rigaInfo: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  info: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' },
  note: { fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'pre-line' },
  statoVisione: { display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, borderRadius: 10, padding: '8px 10px' },
  visioneOk: { background: '#E7F6EC', color: '#1F7A45' },
  visioneAttesa: { background: '#FFF1E0', color: '#8A3B00' },
  visioneMai: { background: 'var(--bg-input)', color: 'var(--text-muted)' },
  boxDest: { display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 },
  rigaDest: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', borderRadius: 12, background: 'var(--bg)' },
  rigaInvio: { display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 10px', borderRadius: 12, background: 'var(--bg)' },
  storico: { display: 'flex', flexDirection: 'column', gap: 3, marginTop: 6 },
  rigaLink: { display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px', borderRadius: 12, background: 'var(--bg)' },
  rigaSede: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, background: 'var(--bg)', flexWrap: 'wrap' },
  finestra: { padding: 20, display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--text)' },
  fieldset: { border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 },
  campi: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 },
  campo: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  etichetta: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--text-muted)', textTransform: 'uppercase' },
  input: { width: '100%', border: '1px solid var(--border2)', borderRadius: 12, padding: '10px 12px', fontSize: 15, color: 'var(--text)', background: 'var(--bg-card)', boxSizing: 'border-box', fontFamily: 'inherit' },
  spunte: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  spunta: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' },
  boxSerie: { display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg-input)', borderRadius: 12, padding: 12 },
  opzioni: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  opz: { border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text)', borderRadius: 999, padding: '7px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 34 },
  opzOn: { borderColor: '#C24B08', background: '#FFF1E0', color: '#8A3B00' },
  opzVis: { border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text)', borderRadius: 12, padding: '8px 12px', fontSize: 13, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, flex: '1 1 150px', textAlign: 'left' },
  opzSpiega: { fontSize: 11, color: 'var(--text-muted)' },
  tagBox: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', border: '1px solid var(--border2)', borderRadius: 12, padding: '6px 8px', background: 'var(--bg-card)', minHeight: 42, boxSizing: 'border-box' },
  tagInput: { flex: '1 1 160px', border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)', fontSize: 14, minWidth: 0, padding: 4 },
  pallino: { width: 26, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', outlineOffset: 2, flex: '0 0 auto' },
  piede: { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' },
  btn: { textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 999, border: '1px solid var(--border2)', background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 36 },
  btnPrimario: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 15px', borderRadius: 999, border: '1px solid #C24B08', background: '#C24B08', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 38 },
  icona: { border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 6, display: 'inline-flex', borderRadius: 8 },
  link: { border: 'none', background: 'none', color: '#C24B08', fontWeight: 700, cursor: 'pointer', fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center', padding: '4px 0' },
  tag: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' },
  tagBlu: { color: '#1D4FA8', background: '#E6EEFB' },
  tagArancio: { color: '#8A3B00', background: '#FFF1E0' },
  tagBozza: { color: '#6B4E00', background: '#FFF4C2', border: '1px dashed #B58900', fontWeight: 700 },
  rigaBozza: { borderStyle: 'dashed', borderLeftStyle: 'dashed', opacity: 0.85 },
  chipBozza: { borderLeftStyle: 'dashed', fontStyle: 'italic', opacity: 0.8 },
  boxBozza: { background: '#FFF4C2', color: '#3D2E00' },
  tagVerde: { color: '#1F7A45', background: '#E7F6EC' },
  tagGrigio: { color: 'var(--text-muted)', background: 'var(--bg-input)' },
  dim: { color: 'var(--text-muted)', fontSize: 13 },
  vuoto: { color: 'var(--text-muted)', padding: 20 },
  err: { background: '#FDE8E8', color: '#9B1C1C', borderRadius: 12, padding: '10px 12px', fontSize: 14 },
};
